import Papa from 'papaparse';
import type {
  CallTreatment,
  Contact,
  EmailAddress,
  NewContact,
  PhoneNumber
} from '@personical/cc-shared-types';
import { CALL_TREATMENTS, countryCode, normaliseAddress } from '@personical/cc-shared-types';

const HEADERS = [
  'displayName',
  'jobTitle',
  'department',
  'company',
  'website',
  'emails',
  'phoneNumbers',
  'tags',
  'callTreatment',
  'notes',
  'street',
  'city',
  'region',
  'postalCode',
  'country'
] as const;

export interface CsvParseResult {
  contacts: NewContact[];
  errors: { row: number; message: string }[];
}

export function parseContactsCsv(text: string): CsvParseResult {
  const parsed = Papa.parse<Record<string, string>>(text, {
    header: true,
    skipEmptyLines: true,
    transformHeader: (h) => h.trim()
  });

  const contacts: NewContact[] = [];
  const errors: { row: number; message: string }[] = [];

  parsed.data.forEach((raw, idx) => {
    try {
      const displayName = (raw.displayName ?? raw.name ?? raw['Display Name'] ?? '').trim();
      if (!displayName) throw new Error('displayName is required');
      const treatmentRaw = (raw.callTreatment ?? raw['Call Treatment'] ?? '')
        .trim()
        .toLowerCase();
      const callTreatment: CallTreatment = (CALL_TREATMENTS as readonly string[]).includes(
        treatmentRaw
      )
        ? (treatmentRaw as CallTreatment)
        : 'normal';
      const countryText = (raw.country ?? raw.Country ?? '').trim();
      const country = countryText ? countryCode(countryText) : undefined;
      if (countryText && !country) {
        throw new Error(`Unknown country "${countryText}": use a two-letter ISO code such as GB`);
      }
      const address = normaliseAddress({
        // Street lines: a multi-line cell, or lines separated by " | ".
        street: (raw.street ?? raw.Street ?? '').replace(/\s*\|\s*/g, '\n'),
        city: raw.city ?? raw.City,
        region: raw.region ?? raw.Region ?? raw.county ?? raw.County ?? raw.state ?? raw.State,
        postalCode: raw.postalCode ?? raw.postcode ?? raw.Postcode ?? raw['Postal Code'] ?? raw.zip,
        country
      });
      if (!address.ok) throw new Error(address.error);
      contacts.push({
        displayName,
        jobTitle: (raw.jobTitle ?? raw['Job Title'] ?? '').trim() || undefined,
        department: (raw.department ?? raw.Department ?? '').trim() || undefined,
        address: address.address,
        company: (raw.company ?? raw.Company ?? '').trim() || undefined,
        website: (raw.website ?? raw.Website ?? raw.url ?? raw.URL ?? '').trim() || undefined,
        emails: parseEmailField(raw.emails ?? raw.email ?? raw.Email ?? ''),
        phoneNumbers: parsePhoneField(raw.phoneNumbers ?? raw.phone ?? raw.Phone ?? ''),
        tags: parseListField(raw.tags ?? raw.Tags ?? ''),
        notes: (raw.notes ?? raw.Notes ?? '').trim() || undefined,
        callTreatment,
        visibility: 'shared',
        kind: 'person',
        custom: {}
      });
    } catch (err) {
      errors.push({ row: idx + 2, message: (err as Error).message }); // +2 = header row + 1-index
    }
  });

  parsed.errors.forEach((e) => errors.push({ row: (e.row ?? -1) + 2, message: e.message }));

  return { contacts, errors };
}

function parseListField(value: string): string[] {
  return value
    .split(/[;|]/)
    .flatMap((v) => v.split(','))
    .map((v) => v.trim())
    .filter(Boolean);
}

function parseEmailField(value: string): EmailAddress[] {
  return parseListField(value).map((entry) => {
    const m = entry.match(/^(work|personal|other):(.+)$/i);
    if (m) return { type: m[1]!.toLowerCase() as EmailAddress['type'], address: m[2]!.trim() };
    return { type: 'work', address: entry };
  });
}

function parsePhoneField(value: string): PhoneNumber[] {
  return parseListField(value).map((entry) => {
    const m = entry.match(/^(mobile|work|home|other):(.+)$/i);
    if (m) return { type: m[1]!.toLowerCase() as PhoneNumber['type'], number: m[2]!.trim() };
    return { type: 'mobile', number: entry };
  });
}

export function contactsToCsv(contacts: Contact[]): string {
  const rows = contacts.map((c) => ({
    displayName: c.displayName,
    jobTitle: c.jobTitle ?? '',
    company: c.company ?? '',
    website: c.website ?? '',
    emails: c.emails.map((e) => `${e.type}:${e.address}`).join('; '),
    phoneNumbers: c.phoneNumbers.map((p) => `${p.type}:${p.number}`).join('; '),
    tags: c.tags.join('; '),
    callTreatment: c.callTreatment ?? 'normal',
    notes: c.notes ?? '',
    department: c.department ?? '',
    street: c.address?.street ?? '',
    city: c.address?.city ?? '',
    region: c.address?.region ?? '',
    postalCode: c.address?.postalCode ?? '',
    country: c.address?.country ?? ''
  }));
  return Papa.unparse(rows, { columns: [...HEADERS] });
}

export function downloadFile(name: string, content: string, mime = 'text/csv') {
  const blob = new Blob([content], { type: `${mime};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export const CSV_TEMPLATE = HEADERS.join(',') + '\n';
